/**
 * Export quality tiers and estimated file size — the part of §13 that is real.
 *
 * WHAT THIS DOES. Bitrate arithmetic. A 4:05 track at 320 kbps is 245 seconds ×
 * 320,000 bits ÷ 8 ÷ 1024 ÷ 1024 ≈ 9.3 MB, and a 44.1kHz 16-bit stereo WAV of the same
 * length is 245 × 44,100 × 2 × 2 bytes ≈ 41.2 MB. Those are correct numbers derived from
 * the track's own length, and they are the answer to "how big will this be?" whether or
 * not a file exists — which is why the estimate is worth showing.
 *
 * WHAT THIS DOES NOT DO: produce a file. There is no audio (`Song.audioUrl` is null for
 * every song) and no encoder, so nothing here returns bytes, a Blob or a URL. The
 * Downloads panel already says plainly that it writes a text details file rather than
 * audio; this module adds the tiers and the size figure to that same honest surface. See
 * api/audioEditApi.ts's `exportAudio` capability.
 *
 * ESTIMATED, and labelled that way everywhere it is shown, for two reasons that are true
 * even with a real encoder behind it: a VBR MP3's size depends on the material, and
 * container overhead (tags, cover art, headers) is a few kilobytes nobody can predict
 * from the length. The figure is right to within a rounding step, not to the byte.
 */

/** Which container an export produces. */
export type ExportFormat = 'mp3' | 'wav';

/** A quality tier. */
export interface ExportProfile {
  id: string;
  label: string;
  format: ExportFormat;
  /** One line for the row, saying who the tier is for. */
  description: string;
  /**
   * Constant bitrate in kbps, for the lossy tiers. Absent for WAV, whose size comes
   * from the sample rate and depth below rather than from a chosen bitrate.
   */
  kbps?: number;
  /** Uncompressed parameters, for the lossless tier. */
  pcm?: { sampleRate: number; bitDepth: number; channels: number };
}

/**
 * The three tiers, in ascending size.
 *
 * MP3 at 192 and 320 rather than a longer ladder: those are the two figures that mean
 * something to people — "fine for sharing" and "as good as MP3 gets" — and a 128 kbps
 * option would exist only to be worse. Lossless is 44.1kHz/16-bit stereo because that is
 * what Lyria 3 actually returns (see api/lyria.ts, where the same figures are recorded
 * against the verified model contract), so a 24-bit tier would claim a depth the source
 * never had.
 */
export const EXPORT_PROFILES: ExportProfile[] = [
  {
    id: 'standard',
    label: 'Standard',
    format: 'mp3',
    description: 'MP3 192 kbps — small, fine for sharing.',
    kbps: 192,
  },
  {
    id: 'high',
    label: 'High',
    format: 'mp3',
    description: 'MP3 320 kbps — the best MP3 quality.',
    kbps: 320,
  },
  {
    id: 'lossless',
    label: 'Lossless',
    format: 'wav',
    description: 'WAV 44.1kHz 16-bit stereo — no compression.',
    pcm: { sampleRate: 44_100, bitDepth: 16, channels: 2 },
  },
];

/** Looks a tier up by id. Falls back to Standard, so a stale id cannot blank the panel. */
export function exportProfile(id: string): ExportProfile {
  return EXPORT_PROFILES.find((p) => p.id === id) ?? EXPORT_PROFILES[0];
}

/**
 * The size of an export, in bytes.
 *
 * Returns 0 for a track with no known length rather than guessing an average song — a
 * zero is visibly not an estimate, whereas "4.1 MB" for an unknown duration is a
 * fabricated number.
 */
export function exportSizeBytes(profile: ExportProfile, seconds: number): number {
  if (!(seconds > 0)) return 0;

  if (profile.pcm) {
    const { sampleRate, bitDepth, channels } = profile.pcm;
    // + 44 bytes of RIFF header. Trivial against tens of megabytes, included because
    // leaving it out would make this "nearly the size" rather than the size.
    return Math.round(seconds * sampleRate * channels * (bitDepth / 8)) + 44;
  }

  if (profile.kbps) {
    // kbps is kilobits per second — 1000 bits, not 1024. The decimal prefix is correct
    // for bitrates and the binary one is correct for the megabytes below, which is the
    // detail that makes a hand-checked estimate come out slightly wrong.
    return Math.round((seconds * profile.kbps * 1000) / 8);
  }

  return 0;
}

/**
 * A byte count in the units a download dialog uses.
 *
 * MB with one decimal for anything over a megabyte, whole KB below that. Not a general
 * byte formatter: an export is never in the gigabytes (a lossless hour would be 600 MB)
 * and never in single bytes, so the two cases here are the two that occur.
 */
export function formatBytes(bytes: number): string {
  if (!(bytes > 0)) return '—';
  const kb = bytes / 1024;
  if (kb < 1024) return `${Math.round(kb)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}

/** The estimate as one phrase for the UI — "Estimated size 9.3 MB". */
export function describeExportSize(profile: ExportProfile, seconds: number): string {
  const bytes = exportSizeBytes(profile, seconds);
  if (bytes === 0) return 'Size unknown — this track has no recorded length.';
  return `Estimated size ${formatBytes(bytes)}`;
}
